import { updateNodeDescription } from '@ui/lib/reactFlow/helpers'
import { useEffect, useMemo, useRef, useState } from 'react'
import SyntaxHighlighter from 'react-syntax-highlighter'
import { a11yLight } from 'react-syntax-highlighter/dist/cjs/styles/hljs'
import { Node, NodeProps } from 'reactflow'
import { useShallow } from 'zustand/react/shallow'

import { NodeStepType } from '@ui/__generated__/graphql'
import { Button } from '@ui/components/core/Button'
import { useLayoutPanelManagerStore, useNodeAndEdgeStore, useNodeStepStore } from '@ui/stores/pipelinesWorkflow'
import { CodeStepConfig } from '@ui/stores/pipelinesWorkflow/NodeStepStore'

import { LayoutPanel } from '../PipelineLayoutManager'
import { CodeEditor } from './CodeEditor'
import { PanelContainer } from './Containers'

export const TransformationCode = ({ node }: { node: NodeProps }) => {
  const codeContainerRef = useRef<HTMLDivElement>(null)

  const [dimensions, setDimensions] = useState({ width: 0, height: 0 })
  const [editCode, setEditCode] = useState(false)
  const [code, setCode] = useState(node.data.code)

  const CONTAINER_PADDING = 34
  const CONTAINER_HEIGHT_OFFSET = 50

  const { setNodes } = useNodeAndEdgeStore(
    useShallow((state) => ({
      setNodes: state.setNodes,
    }))
  )

  useEffect(() => {
    const container = codeContainerRef.current
    if (!container) return

    const resizeObserver = new ResizeObserver((entries) => {
      const { width, height } = entries[0].contentRect

      setDimensions({
        width: width - CONTAINER_PADDING,
        height: height - CONTAINER_HEIGHT_OFFSET,
      })
    })

    resizeObserver.observe(container)
    return () => resizeObserver.disconnect()
  }, [codeContainerRef])

  const { layoutPanelConfig, setPanelContent } = useLayoutPanelManagerStore(
    useShallow((state) => ({
      layoutPanelConfig: state.layoutPanelConfig,
      setPanelContent: state.setPanelContent,
    }))
  )

  const { getConfig, stepId, setConfig } = useNodeStepStore(
    useShallow((state) => ({
      stepId: state.stepId,
      getConfig: state.getConfig,
      setConfig: state.setConfig,
    }))
  )

  const config = useMemo(() => {
    return getConfig(stepId as string, NodeStepType.Code) as CodeStepConfig
  }, [getConfig, node, stepId])

  const isInnerPanel = useMemo(() => {
    return layoutPanelConfig[LayoutPanel.INNER_RIGHT_PANEL].open
  }, [layoutPanelConfig])

  // Open the code editor when the inner panel is open
  useEffect(() => {
    if (isInnerPanel) {
      setEditCode(true)
    }
  }, [isInnerPanel])

  const openCodeEditor = () => {
    setEditCode(true)
  }

  const saveCode = () => {
    setEditCode(false)
  }

  const cancelCode = () => {
    setEditCode(false)
    if (stepId) {
      setConfig(stepId, NodeStepType.Code, { code })
    } else {
      setCode(node.data.code)
      setNodes((nodes) => nodes.map((n) => ({ ...n, data: { ...n.data, code } })))
    }
  }

  const closePanel = () => {
    // TODO create a better way to handle this
    if (isInnerPanel) {
      setPanelContent({ panel: LayoutPanel.INNER_RIGHT_PANEL, content: null, open: false })
    } else {
      setPanelContent({ panel: LayoutPanel.RIGHT_PANEL, content: null, open: false })
    }
  }

  return (
    <PanelContainer
      title="Javascript"
      showDescription={stepId ? false : true}
      description={node.data.description}
      updateDescription={(description) => {
        updateNodeDescription(node as unknown as Node, description)
      }}
      onClose={closePanel}>
      <div className="group size-full space-y-2 p-4" ref={codeContainerRef}>
        <div className="flex h-[calc(100%-3rem)] w-full flex-col">
          {editCode ? (
            <CodeEditor node={node} />
          ) : (
            <SyntaxHighlighter
              style={a11yLight}
              wrapLongLines
              language="javascript"
              customStyle={{
                height: dimensions.height,
              }}
              className="w-full rounded border border-gray-200 p-2 text-xs transition-all duration-75">
              {stepId ? config.code : node.data.code}
            </SyntaxHighlighter>
          )}
        </div>

        <div className="flex h-10 flex-row space-x-2">
          <Button
            variant={editCode ? 'secondary' : 'tertiary'}
            size="small"
            className="w-full"
            onClick={editCode ? cancelCode : openCodeEditor}>
            {editCode ? 'Cancel' : 'Edit code'}
          </Button>
          {editCode && (
            <Button variant="primary" size="small" className="w-full" onClick={saveCode}>
              Save
            </Button>
          )}
        </div>
      </div>
    </PanelContainer>
  )
}
